import React, { useMemo } from 'react';
import { RichTextBlock } from 'prismic-reactjs';
import { ImageProps } from '@components/image';
import Section from '@components/section';
import Wrapper from '@components/wrapper.styled';
import generateId from '@helpers/generate-id';
import Text from '@components/text';
import AnimatedElement from '@components/animated-element';
import {
  StyledHeading,
  StyledImage,
  StyledGrid,
  StyledBlock,
  StyledIcon,
  StyledTextContent,
  Container,
  PaddingWrapper,
  StyledIconImage,
} from './master-dispatch.styled';

interface MasterDispatchItemType {
  icon?: ImageProps;
  title?: {
    text: string;
  };
  description?: {
    richText: RichTextBlock[];
  };
}

export type BackgroundAlignment = 'hidden' | 'left' | 'right' | 'center';

export interface MasterDispatchProps {
  image?: ImageProps;
  title?: string;
  description?: RichTextBlock[];
  items?: MasterDispatchItemType[];
}

const MasterDispatch: React.FC<MasterDispatchProps> = ({
  image,
  title,
  description,
  items = [],
}) => {
  const idGenerator = useMemo(() => generateId(), []);

  const hasItems = useMemo(() => {
    if (!items.length) return false;
    const firstItem = items[0];
    return (
      firstItem?.icon?.url !== null ||
      (firstItem?.description?.richText?.length ?? 0) > 0 ||
      firstItem?.title?.text !== ''
    );
  }, [items]);

  const renderGrid = useMemo(() => {
    if (!items.length) return null;

    return (
      <StyledGrid role="list" aria-label="Feature list">
        {items.map((item, index) => (
          <AnimatedElement
            key={idGenerator.next().value}
            animationType="slideUp"
            delay={index * 0.125}
            threshold={0.1}
            rootMargin="50px"
          >
            <StyledBlock role="listitem">
              <StyledIcon>
                {!!(item.icon?.gatsbyImageData || item.icon?.url) && (
                  <StyledIconImage
                    gatsbyImageData={item.icon?.gatsbyImageData}
                    url={item.icon?.url}
                    alt={item.icon?.alt || item.title?.text || 'Feature icon'}
                    objectFit="contain"
                  />
                )}
              </StyledIcon>

              {!!item.title?.text && (
                <Text
                  type="title"
                  tag="h3"
                  stringText={item.title.text}
                  fontSize="large"
                  fontColor="black"
                  margin="none"
                />
              )}
            </StyledBlock>
          </AnimatedElement>
        ))}
      </StyledGrid>
    );
  }, [items, idGenerator]);

  return (
    <Section backgroundColor="white">
      <Wrapper>
        <Container>
          <PaddingWrapper>
            <StyledTextContent>
              {!!title && (
                <StyledHeading
                  type="title"
                  tag="h2"
                  stringText={title}
                  fontSize="heading2"
                  fontColor="black"
                  margin="none"
                />
              )}
              {!!description && description.length > 0 && description[0]?.text !== '' && (
                <Text
                  type="subheading"
                  richText={description}
                  fontSize="body"
                  fontColor="black"
                  margin="none"
                />
              )}
            </StyledTextContent>
          </PaddingWrapper>
          {!!(image?.gatsbyImageData || image?.url) && (
            <StyledImage
              gatsbyImageData={image?.gatsbyImageData}
              url={image?.url}
              alt={image?.alt || title || 'Decorative image'}
            />
          )}
          <PaddingWrapper>{hasItems && renderGrid}</PaddingWrapper>
        </Container>
      </Wrapper>
    </Section>
  );
};

export default React.memo(MasterDispatch);
